import { FormEvent, useEffect, useState } from 'react';
import { IMicrositeRecipientImage } from '@nkr/models/index';
import { MicrositeType } from '@nkr/enums/index';
import { Button, ButtonToolbar, FormControl, Glyphicon, Panel } from 'react-bootstrap';
import ReactHtmlParser from 'react-html-parser';
import Header from './header';
import Images from './images';
import Footer from './footer';
import VideoRibbon from './video-ribbon';

interface IOwnProps {
    id: number,
    micrositeTypeId: number,
    micrositeType: string,
    shortCode: string,
    firstName: string,
    lastName: string,
    age: number,
    waitListStartDate: string,
    transplantUrgency: string,
    story: string,
    why?: string,
    profile?: any,
    images: IMicrositeRecipientImage[],
    previewImages?: any[],
    starterSite: boolean,
    location: any,
    isIE: boolean,
    isEditable: boolean,
    sendToLivingDonation: () => void,
    onImageClick: (index: number) => void,
    onCommitStory: (story: string) => void,
    onCommitWhy: (why: string) => void
}

const LayoutV2 = (props: IOwnProps) => {
    const [story, setStory] = useState(props.story);
    const [editStory, setEditStory] = useState(false);

    const [why, setWhy] = useState(props.why);
    const [editWhy, setEditWhy] = useState(false);

    const readMore = (triggerButton, elementHeight) => {
        let collapse_content_selector = '#' + document.querySelector(triggerButton).dataset.toggle;
        let containerSelector = document.querySelector(collapse_content_selector);
        let textHeight = containerSelector.scrollHeight;

        if (!containerSelector.classList.contains('expanded')) {
            containerSelector.classList.add('expanded');
            (containerSelector as HTMLElement).style.maxHeight = textHeight + 'px';
            document.querySelector(triggerButton).innerHTML = 'Hide';
        } else {
            containerSelector.classList.remove('expanded');
            (containerSelector as HTMLElement).style.maxHeight = elementHeight;
            document.querySelector(triggerButton).innerHTML = 'Read more...';
        }
    }

    const handleContentLoaded = () => {
        // ROTATOR
        if (document.querySelector('.rotator')?.children.length > 5) {
            document.querySelector('.rotator').classList.add('enabled');
            const mySiema = new (window as any).Siema({
                selector: '.rotator',
                duration: 200,
                easing: 'ease-out',
                loop: true,
                startIndex: 0,
                perPage: {
                    500: 2,
                    769: 4,
                    999: 5
                }
            });
            document.querySelector('.siema-prev').addEventListener('click', () => mySiema.prev());
            document.querySelector('.siema-next').addEventListener('click', () => mySiema.next());
        }
        // END ROTATOR

        // FUNCTION FOR 'READ MORE' BUTTONS

        let overflowContent = document.querySelector('#overflow-content');
        let overflowContentHero = document.querySelector('#overflow-content-hero');

        // For the hero area
        if (overflowContentHero) {
            if (overflowContentHero?.textContent.length > 500) {
                (document.querySelector('.read-more-toggle-hero') as HTMLElement).style.display = 'block';
                document.querySelector('.read-more-toggle-hero').addEventListener('click', function () {
                    readMore('.read-more-toggle-hero', '30rem');
                });
            } else {
                overflowContentHero.classList.remove('overflow-gradient'); //.style.maxHeight = '100%';
                (overflowContentHero as HTMLElement).style.maxHeight = '100%';
            }
        }

        // For the green area
        if (overflowContent) {
            if (overflowContent.textContent.length > 900) {
                (document.querySelector('.read-more-toggle') as HTMLElement).style.display = 'block';
                document.querySelector('.green').classList.add('background-gradient');
                document.querySelector('.read-more-toggle').addEventListener('click', function () {
                    readMore('.read-more-toggle', '41rem');
                });
            } else {
                overflowContent.classList.remove('overflow-gradient'); //
                (overflowContent as HTMLElement).style.maxHeight = '100%';
            }
        }
    }

    const handleStoryChange = (e: FormEvent<FormControl & HTMLTextAreaElement>) => {
        setStory(e.currentTarget.value);
    };

    const handleCommitStory = () => {
        setEditStory(false);
        props.onCommitStory(story);
    };

    const handleCancelEditStory = () => {
        setStory(props.story);
        setEditStory(false);

        setTimeout(() => handleContentLoaded(), 1000);
    };

    const handleWhyChange = (e: FormEvent<FormControl & HTMLTextAreaElement>) => {
        setWhy(e.currentTarget.value);
    };

    const handleCancelEditWhy = () => {
        setWhy(props.why);
        setEditWhy(false);

        setTimeout(() => handleContentLoaded(), 1000);
    };

    const handleCommitWhy = () => {
        setEditWhy(false);
        props.onCommitWhy(why);
    };

    useEffect(() => {
        setTimeout(() => handleContentLoaded(), 1000);
    }, []);

    const profileImageFilename = props.profile ? props.profile.thumbnail || props.profile.fileName : '';
    const images = props.images.filter(x => x.displayOrder > 0);
    const company = props.micrositeTypeId == MicrositeType.Kidney ? "NKR" : "NLR";  

    const callouts = [
        { key: 'lost-wages', icon: 'money-check-pen-blue', title: 'Lost Wage Reimbursement', text: 'replaces wages lost due to time off work' },
        { key: 'expenses', icon: 'house-heart', title: 'Donor Expense Reimbursement', text: 'for travel, lodging, dependent and pet care' },

        ...(props.micrositeTypeId == MicrositeType.Kidney ? [
            { key: 'priority', icon: 'star', title: `${props.micrositeType} Prioritization`, text: `in the unlikely event you ever need a ${props.micrositeType.toLowerCase()}` },
            { key: 'voucher', icon: 'notes-medical', title: 'Voucher Donation', text: 'donate now, protect your family in the future' },
            { key: 'remote', icon: 'map-pin', title: 'Remote Donation', text: 'avoid air travel for donor evaluation & surgery' },
        ] : []),

        { key: 'mentors', icon: 'mentors', title: 'Donor Mentors', text: `speak with a living ${props.micrositeType.toLowerCase()} donor` },

        ...(props.micrositeTypeId == MicrositeType.Kidney ? [
            { key: 'bestmatch', icon: 'user-group', title: 'Best Match', text: 'maximize the impact of your donation' },
        ] : []),

        { key: 'legal', icon: 'scale', title: 'Legal Support', text: 'fighting termination & discrimination' },

        ...(props.micrositeTypeId == MicrositeType.Kidney ? [
            { key: 'complication', icon: 'hand-holding-medical', title: 'Complication Protection', text: 'reimbursement for complications costs' },
            { key: 'reduce-time', icon: 'hourglass', title: 'Reduce Wasted Time', text: 'streamlined donation process' },
            { key: 'blood', icon: 'van', title: 'Home Blood Draws', text: 'eliminate trips to the transplant center' },
        ] : []),

        { key: 'help', icon: 'users', title: 'Help Other Donors', text: 'give feedback to improve the process' },
    ];

    return (
        <>
            <a className="sr-only" href="#micrositescontent">Skip navigation</a>

            <Header
                id={props.id}
                shortCode={props.shortCode}
                firstName={props.firstName}
                lastName={props.lastName}
                micrositeType={props.micrositeType}
                micrositeTypeId={props.micrositeTypeId}
            />

            <main id="micrositescontent" style={{ paddingTop: 0, paddingBottom: 50 }}>
                {/* DELETE IF STARTER SITE */}
                {props.starterSite == false && (
                    <div className="intro-ribbon">
                        <div className="container-fluid">
                            <div className="row">
                                <div className="col main-image" style={{ backgroundImage: "url('" + __NKR_ORG_URL__ + "/microsite/image?filename=" + profileImageFilename + "')" }}></div>
                                <div className="col">
                                    {editStory == false ?
                                        <>
                                            <blockquote id="overflow-content-hero" className="overflow-gradient">
                                                <p>{ReactHtmlParser(props.story.replace(/(?:\r\n|\r|\n)/g, '<br/>'))}</p>
                                            </blockquote>
                                            <button type="button" data-toggle="overflow-content-hero" className="read-more-toggle-hero">Read more...</button>
                                            {props.isEditable && (
                                                <div style={{ paddingLeft: '3.8rem', marginLeft: '5rem' }}>
                                                    <Button title='Edit this section' onClick={() => setEditStory(true)}><Glyphicon glyph='pencil' style={{ fontFamily: 'Glyphicons Halflings' }} /></Button>
                                                </div>
                                            )}
                                        </>
                                        :
                                        <div style={{ width: 500 }}>
                                            <Panel>
                                                <FormControl
                                                    componentClass='textarea'
                                                    style={{ height: 500, resize: 'none', border: '0 none' }}
                                                    value={story}
                                                    onChange={handleStoryChange}
                                                />
                                                <Panel.Footer>
                                                    <ButtonToolbar style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                        <Button bsStyle='primary' onClick={handleCommitStory}>Commit Changes</Button>
                                                        <Button onClick={handleCancelEditStory}>Cancel</Button>
                                                    </ButtonToolbar>
                                                </Panel.Footer>
                                            </Panel>
                                        </div>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {/* /END */}

                <div className="green-rotator-container">
                    {/* DELETE IF STARTER SITE */}
                    {props.starterSite == false && (
                        <Images images={images} previewImages={[]} />
                    )}
                    {/* /END */}

                    <div className="green">
                        <div className="container-fluid">
                            <div className="row">
                                <section className="col-sm-6">
                                    {editWhy == false ?
                                        <>
                                            <h2>Why I Need a {props.micrositeType}</h2>
                                            <div id="overflow-content" className="overflow-gradient">
                                                <p>{ReactHtmlParser(props.why.replace(/(?:\r\n|\r|\n)/g, '<br/>'))}</p>
                                            </div>
                                            <button type="button" data-toggle="overflow-content" className="read-more-toggle">Read more...</button>
                                            {props.isEditable && (
                                                <div><Button title='Edit this section' onClick={() => setEditWhy(true)}><Glyphicon glyph='pencil' style={{ fontFamily: 'Glyphicons Halflings' }} /></Button></div>
                                            )}
                                        </>
                                        :
                                        <Panel>
                                            <FormControl
                                                componentClass='textarea'
                                                style={{ height: 500, resize: 'none', border: '0 none' }}
                                                value={why}
                                                onChange={handleWhyChange}
                                            />
                                            <Panel.Footer>
                                                <ButtonToolbar style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                    <Button bsStyle='primary' onClick={handleCommitWhy}>Commit Changes</Button>
                                                    <Button onClick={handleCancelEditWhy}>Cancel</Button>
                                                </ButtonToolbar>
                                            </Panel.Footer>
                                        </Panel>
                                    }
                                </section>
                                <section className="col-sm-6 col-md-5 col-sm-offset-0 col-md-offset-1 white-background">
                                    <h2>How You Can Help</h2>
                                    <p>By sharing my story to your group of family and friends, you help to increase the number of people who may consider {props.micrositeType.toLowerCase()} donation.</p>
                                    <p>You can also support me and other patients in need of a transplant by being screened to become a {props.micrositeType.toLowerCase()} donor with the {company}. The process is easy to complete and potential donors have access to the {company}&rsquo;s nationwide donor support network.</p>
                                    <div style={{ textAlign: 'center' }}>
                                        <a className='btn' onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>Start the Donor Screening Process</a>
                                        <a className="btn outline arrow-down" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>Donor Support &amp; Protections</a>
                                    </div>
                                </section>
                            </div>
                        </div>
                    </div>
                </div>

                <section className="callouts" id="supportcallouts">
                    <div className="container-fluid">
                        <div className="row">
                            <h2><b>Support &amp; Protections</b> Available to Donors through the <b>National Kidney Registry</b></h2>
                        </div>
                        <div className="row">
                            <ul style={{ listStyleType: 'none' }}>

                                {callouts.map((item, index) => {
                                    const columns = 4; // must match your CSS grid
                                    const row = Math.floor(index / columns);
                                    const col = index % columns;

                                    const colorClass = (row + col) % 2 == 0 ? 'tile-blue' : 'tile-green';

                                    return (
                                        <li key={item.key} className={`icon ${item.icon} ${colorClass}`}>
                                            <b>{item.title}</b>
                                            <p>{item.text}</p>
                                        </li>
                                    );
                                })}


                                {/*<li className="icon money-check-pen">*/}
                                {/*    <b>Cost Reimbursement</b>*/}
                                {/*    <p>lost wages, travel &amp; lodging</p>*/}
                                {/*</li>*/}

                                {/*{props.micrositeTypeId == MicrositeType.Kidney && (*/}
                                {/*    <li className="icon star">*/}
                                {/*        <b>{props.micrositeType} Prioritization</b>*/}
                                {/*        <p>in the unlikely event you ever need a {props.micrositeType.toLowerCase()}</p>*/}
                                {/*    </li>*/}
                                {/*)}*/}

                                {/*{props.micrositeTypeId == MicrositeType.Kidney && (*/}
                                {/*    <li className="icon notes-medical">*/}
                                {/*        <b>Voucher Donation</b>*/}
                                {/*        <p>the future of paired exchange</p>*/}
                                {/*    </li>*/}
                                {/*)}*/}

                                {/*{props.micrositeTypeId == MicrositeType.Kidney && (*/}
                                {/*    <li className="icon bed">*/}
                                {/*        <b>Remote Donation</b>*/}
                                {/*        <p>avoid air travel for donor evaluation &amp; surgery</p>*/}
                                {/*    </li>*/}
                                {/*)}*/}

                                {/*{props.micrositeTypeId == MicrositeType.Kidney && (*/}
                                {/*    <li className="icon user-group">*/}
                                {/*        <b>Best Match</b>*/}
                                {/*        <p>maximize the impact of your donation</p>*/}
                                {/*    </li>*/}
                                {/*)}*/}

                                {/*<li className="icon scale">*/}
                                {/*    <b>Legal Support</b>*/}
                                {/*    <p>fighting termination &amp; discrimination</p>*/}
                                {/*</li>*/}

                                {/*{props.micrositeTypeId == MicrositeType.Kidney && (*/}
                                {/*    <li className="icon hand-holding-medical">*/}
                                {/*        <b>Complication Protection</b>*/}
                                {/*        <p>reimbursement for complications costs</p>*/}
                                {/*    </li>*/}
                                {/*)}*/}

                                {/*{props.micrositeTypeId == MicrositeType.Kidney && (*/}
                                {/*    <li className="icon hourglass">*/}
                                {/*        <b>Reduce Wasted Time</b>*/}
                                {/*        <p>streamlined donation process</p>*/}
                                {/*    </li>*/}
                                {/*)}*/}

                                {/*{props.micrositeTypeId == MicrositeType.Kidney && (*/}
                                {/*    <li className="icon van">*/}
                                {/*        <b>Home Blood Draws</b>*/}
                                {/*        <p>eliminate trips to the transplant center</p>*/}
                                {/*    </li>*/}
                                {/*)}*/}

                                {/*<li className="icon users">*/}
                                {/*    <b>Help Other Donors</b>*/}
                                {/*    <p>give feedback to improve the process</p>*/}
                                {/*</li>*/}



                            </ul>
                        </div>
                        <div className="row buttons">
                            <a className="btn" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>Looking to Donate for {props.firstName}?</a>
                            <a className="btn outline" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>Learn more about Donor Support &amp; Protections</a>
                        </div>
                        <div>
                            <br />
                            {props.micrositeTypeId == MicrositeType.Kidney && (
                                <VideoRibbon />
                            )}
                        </div>
                    </div>
                </section>
            </main >

            <Footer location={props.location} />
        </>
    );
}

export default LayoutV2;